/**
 * NCR Create Page - Create a new Non-Conformance Report
 * Based on Portal v1.89 NCR implementation
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ArrowLeft, Loader2, Save, Search, CheckCircle2, FileText, SkipForward } from 'lucide-react';
import { qualityAPI, suppliersAPI, purchasingAPI, coreAPI, platformSettingsAPI } from '../lib/api';
import type { SourceOfRejection, FaultCodeGroup, FaultCodeCategory, UserProfile, QualityTabsVisibility } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

interface NCRFormData {
  supplier: number | null;
  ncr_number: string;
  part_number: string;
  description: string;
  batch_number: string;
  batch_qty: number;
  reject_qty: number;
  shortage_qty: number;
  defect_description: string;
  grn_number: string;
  po_number: string;
  unit_price: string;
  works_order: string;
  po_line: number | null;
  po_line_number: string;
  grn: number | null;
  source_of_rejection_option: number | null;
  fault_group: number | null;
  fault_category: number | null;
  liability_type: number;
  is_collection_required: boolean;
  is_credit_required: boolean;
  buyer_name: string;
}

export default function NCRCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [grnLinked, setGrnLinked] = useState(false);
  const [customerUsers, setCustomerUsers] = useState<UserProfile[]>([]);

  // Tab visibility check
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // Check if NCRs tab is enabled
  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if NCRs tab is disabled
  useEffect(() => {
    if (!tabSettingsLoaded) return;
    if (!tabVisibility.ncrs) {
      navigate('/quality', { replace: true });
    }
  }, [tabSettingsLoaded, tabVisibility.ncrs, navigate]);

  // Classification dropdown options
  const [sourcesOfRejection, setSourcesOfRejection] = useState<SourceOfRejection[]>([]);
  const [faultGroups, setFaultGroups] = useState<FaultCodeGroup[]>([]);
  const [faultCategories, setFaultCategories] = useState<FaultCodeCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<FaultCodeCategory[]>([]);

  // GRN dialog state
  const [showGrnDialog, setShowGrnDialog] = useState(true);
  const [grnInput, setGrnInput] = useState('');
  const [lookingUpGRN, setLookingUpGRN] = useState(false);

  const [formData, setFormData] = useState<NCRFormData>({
    supplier: null,
    ncr_number: '',
    part_number: '',
    description: '',
    batch_number: '',
    batch_qty: 0,
    reject_qty: 0,
    shortage_qty: 0,
    defect_description: '',
    grn_number: '',
    po_number: '',
    unit_price: '',
    works_order: '',
    po_line: null,
    po_line_number: '',
    grn: null,
    source_of_rejection_option: null,
    fault_group: null,
    fault_category: null,
    liability_type: 0,
    is_collection_required: false,
    is_credit_required: false,
    buyer_name: '',
  });

  // Fetch suppliers and classification options
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await suppliersAPI.getProfiles({ page_size: 1000, minimal: true });
        const mapped = response.data.results.map(p => ({ id: p.company_id, name: p.company_name }));
        const unique = Array.from(new Map(mapped.map(s => [s.id, s])).values())
          .sort((a, b) => a.name.localeCompare(b.name));
        setSuppliers(unique);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to load suppliers');
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const fetchClassificationOptions = async () => {
      try {
        const [sorResp, fgResp, fcResp] = await Promise.all([
          qualityAPI.getSourcesOfRejection({ is_active: true, page_size: 200 }),
          qualityAPI.getFaultGroups({ is_active: true, page_size: 200 }),
          qualityAPI.getFaultCategories({ is_active: true, page_size: 500 }),
        ]);
        setSourcesOfRejection(sorResp.data.results);
        setFaultGroups(fgResp.data.results);
        setFaultCategories(fcResp.data.results);
      } catch (error) {
        console.error('Error fetching classification options:', error);
      }
    };

    const fetchCustomerUsers = async () => {
      if (user?.company) {
        try {
          const response = await coreAPI.getCompanyUsers(user.company);
          setCustomerUsers(response.data);
        } catch {
          console.error('Error fetching customer users');
        }
      }
    };

    fetchSuppliers();
    fetchClassificationOptions();
    fetchCustomerUsers();
  }, [user?.company]);

  const updateField = (field: keyof NCRFormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'grn_number') {
      setGrnLinked(false);
      setFormData(prev => ({ ...prev, [field]: value as string, po_line: null, po_line_number: '', grn: null }));
    }
    // When fault group changes, filter categories and reset category selection
    if (field === 'fault_group') {
      const groupId = value as number | null;
      if (groupId) {
        setFilteredCategories(faultCategories.filter(c => c.fault_group === groupId));
      } else {
        setFilteredCategories([]);
      }
      setFormData(prev => ({ ...prev, fault_group: groupId, fault_category: null }));
    }
  };

  const doGRNLookup = useCallback(async (grnNumber: string, suppliersList: Array<{ id: number; name: string }>) => {
    try {
      setLookingUpGRN(true);
      const response = await purchasingAPI.getGRNs({ search: grnNumber, page_size: 5 });
      const grns = response.data.results;

      if (!grns || grns.length === 0) {
        toast.error(`No GRN found matching "${grnNumber}"`);
        return false;
      }

      const grn = grns.find(g => g.grn_number === grnNumber) || grns[0];
      // Cast to access all fields returned by API (some not in TS type)
      const grnData = grn as unknown as Record<string, unknown>;

      const updates: Partial<NCRFormData> = {
        grn: grn.id,
        grn_number: grn.grn_number || grnNumber,
      };

      // Populate from top-level GRN fields (API returns these directly)
      if (grn.po_number) updates.po_number = grn.po_number;
      if (grn.line_number) updates.po_line_number = String(grn.line_number);
      if (grn.part_number) updates.part_number = grn.part_number;
      if (grnData['description']) updates.description = String(grnData['description']);
      if (grn.part_description) updates.description = grn.part_description;
      if (grn.unit_price) updates.unit_price = String(grn.unit_price);
      if (grn.batch_number) updates.batch_number = grn.batch_number;

      // Set po_line FK (API may return as po_line or po_line_id)
      const poLineId = grn.po_line || (grnData['po_line_id'] as number | undefined);
      if (poLineId) {
        updates.po_line = poLineId;
        // Try to fetch PO line for additional details (supplier, etc.)
        try {
          const lineResp = await purchasingAPI.getLine(poLineId);
          const line = lineResp.data;
          if (line.po_number && !updates.po_number) updates.po_number = line.po_number;
          if (line.line_number && !updates.po_line_number) updates.po_line_number = String(line.line_number);
          if (line.part_number && !updates.part_number) updates.part_number = line.part_number;
          if (line.description && !updates.description) updates.description = line.description;
          if (line.unit_price && !updates.unit_price) updates.unit_price = String(line.unit_price);
          if (line.supplier_id) updates.supplier = line.supplier_id;
        } catch {
          // PO line lookup failed, use what we have from GRN
        }
      }

      // Fallback: resolve supplier from name if not set from PO line
      if (grn.supplier_name && !updates.supplier) {
        const match = suppliersList.find(s => s.name === grn.supplier_name);
        if (match) updates.supplier = match.id;
      }

      setFormData(prev => ({ ...prev, ...updates }));
      setGrnLinked(true);
      toast.success('GRN data loaded - supplier, PO, and part details populated');
      return true;
    } catch (error) {
      console.error('Error looking up GRN:', error);
      toast.error('Failed to look up GRN');
      return false;
    } finally {
      setLookingUpGRN(false);
    }
  }, []);

  const handleDialogLookup = async () => {
    const trimmed = grnInput.trim();
    if (!trimmed) {
      toast.error('Please enter a GRN number');
      return;
    }
    const success = await doGRNLookup(trimmed, suppliers);
    if (success) {
      setShowGrnDialog(false);
    }
  };

  const handleSkipGrn = () => {
    setShowGrnDialog(false);
  };

  const handleSubmit = async () => {
    if (!formData.supplier) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.source_of_rejection_option) {
      toast.error('Please select a source of rejection');
      return;
    }
    if (!formData.fault_group) {
      toast.error('Please select a fault group');
      return;
    }
    if (!formData.fault_category) {
      toast.error('Please select a fault category');
      return;
    }
    if (!formData.defect_description.trim()) {
      toast.error('Please enter a defect description');
      return;
    }

    try {
      setSaving(true);

      const apiData: Record<string, unknown> = {
        supplier: formData.supplier,
        defect_description: formData.defect_description,
      };

      if (formData.ncr_number) apiData['ncr_number'] = formData.ncr_number;
      if (formData.part_number) apiData['part_number'] = formData.part_number;
      if (formData.description) apiData['description'] = formData.description;
      if (formData.batch_number) apiData['batch_number'] = formData.batch_number;
      if (formData.grn_number) apiData['grn_number'] = formData.grn_number;
      if (formData.po_number) apiData['po_number'] = formData.po_number;
      if (formData.works_order) apiData['works_order'] = formData.works_order;
      if (formData.unit_price) apiData['unit_price'] = formData.unit_price;
      if (formData.po_line) apiData['po_line'] = formData.po_line;
      if (formData.grn) apiData['grn'] = formData.grn;
      apiData['source_of_rejection_option'] = formData.source_of_rejection_option;
      apiData['fault_group'] = formData.fault_group;
      apiData['fault_category'] = formData.fault_category;
      apiData['liability_type'] = formData.liability_type;
      apiData['is_collection_required'] = formData.is_collection_required;
      apiData['is_credit_required'] = formData.is_credit_required;
      if (formData.buyer_name) apiData['buyer_name'] = formData.buyer_name;

      apiData['batch_qty'] = formData.batch_qty || 0;
      apiData['reject_qty'] = formData.reject_qty || 0;
      apiData['shortage_qty'] = formData.shortage_qty || 0;

      const response = await qualityAPI.createNCR(apiData);
      toast.success('NCR created successfully');
      navigate(`/quality/ncr/${response.data.id}/edit`);
    } catch (error) {
      console.error('Error creating NCR:', error);
      toast.error('Failed to create NCR');
    } finally {
      setSaving(false);
    }
  };

  // Show loading while checking tab settings
  if (!tabSettingsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* GRN Lookup Dialog */}
      <Dialog open={showGrnDialog} onOpenChange={setShowGrnDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link a Goods Receipt Note</DialogTitle>
            <DialogDescription>
              Do you have a GRN number? Entering it will auto-populate supplier, PO, part, and pricing details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="grn_dialog_input">GRN Number</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="grn_dialog_input"
                  value={grnInput}
                  onChange={(e) => setGrnInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleDialogLookup(); } }}
                  placeholder="e.g. GRN-620252-10"
                  autoFocus
                />
                <Button
                  onClick={handleDialogLookup}
                  disabled={lookingUpGRN || !grnInput.trim()}
                >
                  {lookingUpGRN ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleSkipGrn} className="w-full sm:w-auto">
              <SkipForward className="h-4 w-4 mr-2" />
              Skip - No GRN
            </Button>
            <Button onClick={handleDialogLookup} disabled={lookingUpGRN || !grnInput.trim()} className="w-full sm:w-auto">
              {lookingUpGRN ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Looking up...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Look up GRN
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/quality')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create New NCR</h1>
          <p className="text-muted-foreground">Create a new Non-Conformance Report</p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>NCR Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Row 1: GRN and Site NCR */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="grn_number">GRN No.</Label>
              <div className="flex gap-2">
                <Input
                  id="grn_number"
                  value={formData.grn_number}
                  onChange={(e) => updateField('grn_number', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (formData.grn_number.trim()) {
                        doGRNLookup(formData.grn_number.trim(), suppliers);
                      }
                    }
                  }}
                  placeholder="Enter GRN number to auto-fill..."
                  className={grnLinked ? 'border-green-500' : ''}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (formData.grn_number.trim()) {
                      doGRNLookup(formData.grn_number.trim(), suppliers);
                    }
                  }}
                  disabled={lookingUpGRN || !formData.grn_number.trim()}
                  title="Look up GRN to auto-populate fields"
                >
                  {lookingUpGRN ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : grnLinked ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {grnLinked && (
                <p className="text-xs text-green-600 mt-1">GRN linked - fields auto-populated</p>
              )}
            </div>
            <div>
              <Label htmlFor="ncr_number">Company NCR/SRN No.</Label>
              <Input
                id="ncr_number"
                value={formData.ncr_number}
                onChange={(e) => updateField('ncr_number', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Row 2: Supplier */}
          <div>
            <Label htmlFor="supplier">Supplier *</Label>
            <select
              id="supplier"
              value={formData.supplier || ''}
              onChange={(e) => updateField('supplier', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              disabled={loadingSuppliers}
            >
              <option value="">Select a supplier...</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          {/* Row 3: PO No. and PO Line No. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="po_number">PO No.</Label>
              <Input
                id="po_number"
                value={formData.po_number}
                onChange={(e) => updateField('po_number', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="po_line_number">PO Line No.</Label>
              <Input
                id="po_line_number"
                value={formData.po_line_number}
                onChange={(e) => updateField('po_line_number', e.target.value)}
                placeholder={grnLinked ? '' : 'Auto-filled from GRN'}
                readOnly={grnLinked}
                className={grnLinked ? 'bg-muted' : ''}
              />
            </div>
          </div>

          {/* Row 3: Part Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="part_number">Part No.</Label>
              <Input
                id="part_number"
                value={formData.part_number}
                onChange={(e) => updateField('part_number', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="batch_number">Date Code/Batch No.</Label>
              <Input
                id="batch_number"
                value={formData.batch_number}
                onChange={(e) => updateField('batch_number', e.target.value)}
              />
            </div>
          </div>

          {/* Row 4: Part Description */}
          <div>
            <Label htmlFor="description">Part Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
            />
          </div>

          {/* Row 5: Unit Price and Works Order */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="unit_price">Unit Price</Label>
              <Input
                id="unit_price"
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_price}
                onChange={(e) => updateField('unit_price', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="works_order">Works Order No.</Label>
              <Input
                id="works_order"
                value={formData.works_order}
                onChange={(e) => updateField('works_order', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Row 6: Classification (mandatory) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="source_of_rejection_option">Source of Rejection *</Label>
              <select
                id="source_of_rejection_option"
                value={formData.source_of_rejection_option || ''}
                onChange={(e) => updateField('source_of_rejection_option', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select...</option>
                {sourcesOfRejection.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="fault_group">Fault Group *</Label>
              <select
                id="fault_group"
                value={formData.fault_group || ''}
                onChange={(e) => updateField('fault_group', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select...</option>
                {faultGroups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="fault_category">Fault Category *</Label>
              <select
                id="fault_category"
                value={formData.fault_category || ''}
                onChange={(e) => updateField('fault_category', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                disabled={!formData.fault_group}
              >
                <option value="">{formData.fault_group ? 'Select...' : 'Select fault group first'}</option>
                {filteredCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 7: Buyer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="buyer_name">Buyer</Label>
              <select
                id="buyer_name"
                value={formData.buyer_name}
                onChange={(e) => updateField('buyer_name', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select Buyer...</option>
                {customerUsers.map((u) => (
                  <option key={u.id} value={`${u.user.first_name} ${u.user.last_name}`.trim()}>
                    {`${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 8: Liability & Requirements */}
          <div className="border-t pt-4 mt-2">
            <h4 className="text-sm font-semibold mb-1">Liability <span className="text-red-500">*</span></h4>
            <p className="text-xs text-muted-foreground mb-3">Please Select</p>
            <div className="grid grid-cols-2 gap-3 max-w-lg">
              <button
                type="button"
                onClick={() => updateField('liability_type', 2)}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                  formData.liability_type === 2
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
                }`}
              >
                Customer Liability
              </button>
              <button
                type="button"
                onClick={() => updateField('liability_type', 1)}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                  formData.liability_type === 1
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
                }`}
              >
                Supplier Liability
              </button>
            </div>

            {formData.liability_type > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-3">
                  Required actions for the {formData.liability_type === 1 ? 'supplier' : 'customer'}:
                </p>
                <div className={`grid ${formData.liability_type === 1 ? 'grid-cols-3' : 'grid-cols-2'} gap-3 max-w-lg`}>
                  <div>
                    <Label className="text-xs">8D Required <span className="text-red-500">*</span></Label>
                    <select
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                      defaultValue=""
                    >
                      <option value="">Please Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Collection Required <span className="text-red-500">*</span></Label>
                    <select
                      value={formData.is_collection_required ? 'yes' : ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_collection_required: e.target.value === 'yes' }))}
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Please Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  {formData.liability_type === 1 && (
                    <div>
                      <Label className="text-xs">Credit Required <span className="text-red-500">*</span></Label>
                      <select
                        value={formData.is_credit_required ? 'yes' : ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_credit_required: e.target.value === 'yes' }))}
                        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="">Please Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Row 8: Quantities */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="batch_qty">Batch Qty</Label>
              <Input
                id="batch_qty"
                type="number"
                value={formData.batch_qty}
                onChange={(e) => updateField('batch_qty', parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label htmlFor="reject_qty">Reject Qty</Label>
              <Input
                id="reject_qty"
                type="number"
                value={formData.reject_qty}
                onChange={(e) => updateField('reject_qty', parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label htmlFor="shortage_qty">Shortage Qty</Label>
              <Input
                id="shortage_qty"
                type="number"
                value={formData.shortage_qty}
                onChange={(e) => updateField('shortage_qty', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Row 7: Defect Description */}
          <div>
            <Label htmlFor="defect_description">Defect Description *</Label>
            <Textarea
              id="defect_description"
              value={formData.defect_description}
              onChange={(e) => updateField('defect_description', e.target.value)}
              rows={4}
              placeholder="Describe the defect or non-conformance..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate('/quality')}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create NCR
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
